import { Input } from "@/components/basic";
import { useReserveForm } from "@/components/reserve/Reserve/ReserveFormProvider";
import { WPRequests } from "@/components/reserve/WPRequests";
import { useWorkplaceRequestTypes } from "@/hooks/useWorkplaceRequestTypes";
import { isWorkplaceRequestsEnabled } from "@/lib/featureFlags";
import { WorkplaceRequestsRef } from "@/types/workplace-requests";
import { Button } from "@gettactic/components/src/@v2/components/ui/button";
import { Plus, Search } from "lucide-react";

import { useIsDesignSystem2, useOrgFeatureFlag } from "@gettactic/hooks";
import React, { useState, useMemo } from "react";
import { WorkplaceRequestReserveFormData } from "@gettactic/helpers/src/reserve/mapWorkplaceRequestToReserveFormData";

export const RequestTypeSelector: React.FC<{
  onTypeSelect: (typeId: string) => void;
  selectedTypes: string[];
  className?: string;
}> = ({ onTypeSelect, selectedTypes, className }) => {
  const { allElements: types, isFetching: isLoading } =
    useWorkplaceRequestTypes();
  const [isExpanded, setIsExpanded] = useState(false);
  const [filterText, setFilterText] = useState("");

  // Filter out already selected types
  const availableTypes = types.filter(
    (type) => !selectedTypes.includes(type.id)
  );

  // Filter types based on search text
  const filteredTypes = useMemo(() => {
    if (!filterText.trim()) return availableTypes;
    return availableTypes.filter(
      (type) =>
        type.name.toLowerCase().includes(filterText.toLowerCase()) ||
        type.description?.toLowerCase().includes(filterText.toLowerCase())
    );
  }, [availableTypes, filterText]);

  const handleTypeClick = (typeId: string) => {
    onTypeSelect(typeId);
    setIsExpanded(false);
    setFilterText("");
  };

  const handleExpandToggle = () => {
    setIsExpanded(!isExpanded);
    if (isExpanded) {
      setFilterText("");
    }
  };

  if (isLoading) {
    return (
      <div
        className={`flex items-center justify-between gap-1 ${className || ""}`}
      >
        <div>
          <span className="font-inter font-normal text-sm">
            Add Workplace Request(s)
          </span>
        </div>
        <Button disabled className="min-w-[120px]">
          Loading...
        </Button>
      </div>
    );
  }

  if (availableTypes.length === 0) {
    return (
      <div
        className={`flex items-center justify-between gap-1 ${className || ""}`}
      >
        <div>
          <span className="font-inter font-normal text-sm">
            Add Workplace Request(s)
          </span>
        </div>
        <Button variant="outline" disabled className="min-w-[120px]">
          No more types available
        </Button>
      </div>
    );
  }

  return (
    <div className={`space-y-2 ${className || ""}`}>
      <div className="flex items-center justify-between gap-1">
        <div>
          <span className="font-inter font-normal text-sm">
            Add Workplace Request(s)
          </span>
        </div>
        <Button
          onClick={handleExpandToggle}
          variant="outline"
          size="sm"
          className="min-w-[120px]"
          type="button"
        >
          <Plus className="mr-1 h-4 w-4" />
          Add request
        </Button>
      </div>

      {isExpanded && (
        <div className="relative overflow-hidden rounded-lg border-1 border-neutral-100 bg-[#F7F9FB] p-4 font-inter text-muted-foreground">
          <div className="mb-3">
            <div className="relative">
              <Search className="absolute top-0 mt-2 left-3 z-10 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                className="h-8 border-neutral-200 bg-white pl-10 text-sm focus:border-primary focus:ring-1 focus:ring-primary"
              />
            </div>
          </div>

          <div className="scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-300 hover:scrollbar-thumb-gray-400 max-h-[120px] space-y-0.5 overflow-y-auto">
            {filteredTypes.length === 0 ? (
              <div className="py-2 text-center text-muted-foreground text-sm">
                No matching request types found
              </div>
            ) : (
              filteredTypes.map((type) => (
                <button
                  key={type.id}
                  onClick={() => handleTypeClick(type.id)}
                  className="group w-full rounded-md px-2 py-1.5 text-left transition-colors duration-150 hover:bg-gray-100"
                  type="button"
                >
                  <div className="">
                    <div className="font-medium text-gray-900 text-sm group-hover:text-primary">
                      {type.name}
                    </div>
                    {type.description && (
                      <div className="text-muted-foreground text-xs whitespace-break-spaces">
                        {type.description}
                      </div>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export interface ReserveFormWPRequestsProps {
  workplaceRequestItems?: WorkplaceRequestReserveFormData[];
}

export const ReserveFormWPRequests: React.FC<ReserveFormWPRequestsProps> = ({
  workplaceRequestItems
}) => {
  const { office, wpRequestsRef, form, setCurrentBufferSettings } =
    useReserveForm();

  const workplaceRequestsFeatureFlag = useOrgFeatureFlag("workplace_requests");
  const shouldShow = isWorkplaceRequestsEnabled(workplaceRequestsFeatureFlag);
  const isV2 = useIsDesignSystem2();

  if (!shouldShow || !isV2) {
    return null;
  }

  if (!office) {
    return null;
  }

  const recurringValue = form.watch("recurring");
  const isRecurring =
    recurringValue && recurringValue !== "" && recurringValue !== "no";

  if (isRecurring) {
    return null;
  }

  return (
    <div className="" onClick={(e) => e.stopPropagation()}>
      <WPRequests
        officeId={office.id}
        onSubmitRef={
          wpRequestsRef as React.MutableRefObject<
            WorkplaceRequestsRef | undefined
          >
        }
        className=""
        customTypeSelector={RequestTypeSelector}
        workplaceRequestItems={workplaceRequestItems}
        onBufferChange={setCurrentBufferSettings}
      />
    </div>
  );
};
